import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Search, Check } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import { JOURNAL } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function readingLabel(mins) {
  if (!mins) return "1 min read";
  return `${mins} min read`;
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function ArticleCard({ a, size = "md" }) {
  const isLg = size === "lg";
  return (
    <Link
      to={`/journal/${a.slug}`}
      data-testid={JOURNAL.articleLink}
      className="group block"
    >
      <article
        className="flex flex-col h-full"
        data-testid={JOURNAL.articleCard}
      >
        <div
          className={`overflow-hidden bg-[#f4f4f4] ${
            isLg ? "aspect-[16/10]" : "aspect-[4/3]"
          }`}
        >
          {a.cover_image && (
            <img
              src={a.cover_image}
              alt={a.title}
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              referrerPolicy="no-referrer"
            />
          )}
        </div>
        <div className={`${isLg ? "pt-7 md:pt-8" : "pt-5"} flex-1 flex flex-col`}>
          <div className="eyebrow eyebrow-denim mb-3">{a.category}</div>
          <h3
            className={`font-display font-extrabold tracking-[-0.03em] leading-[1.08] text-ink group-hover:text-denim-bnm transition-colors ${
              isLg
                ? "text-[28px] sm:text-[36px] md:text-[44px]"
                : "text-[20px] md:text-[22px]"
            }`}
          >
            {a.title}
          </h3>
          {a.dek && (
            <p
              className={`text-muted-ink leading-[1.6] mt-3 ${
                isLg ? "text-[16px] md:text-[17px]" : "text-[14px]"
              }`}
            >
              {a.dek}
            </p>
          )}
          <div className="mt-5 flex items-center gap-2 text-[12px] text-muted-ink tracking-[0.02em]">
            <span>{formatDate(a.published_at || a.created_at)}</span>
            <span className="w-1 h-1 rounded-full bg-muted-ink/50" />
            <span>{readingLabel(a.reading_time)}</span>
            {a.brand && (
              <>
                <span className="w-1 h-1 rounded-full bg-muted-ink/50" />
                <span className="italic">{a.brand}</span>
              </>
            )}
          </div>
        </div>
      </article>
    </Link>
  );
}

function ArticleGrid({ loading, articles }) {
  if (loading && articles.length === 0) {
    return <div className="text-muted-ink text-[14px]">Loading stories…</div>;
  }
  if (articles.length === 0) {
    return (
      <div
        className="text-muted-ink text-[15px] max-w-[520px]"
        data-testid={JOURNAL.empty}
      >
        Nothing yet under this thread. Try a different category, or clear your
        search.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14 md:gap-y-20">
      {articles.map((a, i) => (
        <Reveal key={a.id} delay={(i % 3) * 100}>
          <ArticleCard a={a} />
        </Reveal>
      ))}
    </div>
  );
}

function SubscribeButtonLabel({ state }) {
  if (state === "done") {
    return (
      <>
        <Check className="w-4 h-4" /> You&rsquo;re on the list
      </>
    );
  }
  if (state === "sending") return "Sending…";
  return (
    <>
      Subscribe <ArrowRight className="w-4 h-4" />
    </>
  );
}

function Newsletter() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | done | error
  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) return;
    setState("sending");
    try {
      await axios.post(`${API}/journal/subscribe`, { email, source: "journal" });
      setState("done");
    } catch {
      setState("error");
    }
  };
  return (
    <section className="border-t border-bnm bg-[#fafafa]">
      <div className="max-w-[900px] mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
        <Reveal>
          <div className="eyebrow eyebrow-denim mb-6">The Daily Dispatch</div>
        </Reveal>
        <Reveal as="h2" delay={80} className="font-display font-extrabold text-[36px] sm:text-[52px] md:text-[68px] tracking-[-0.045em] leading-[1.02] text-ink">
          Stay <span className="font-serif-italic font-light">creatively</span>{" "}
          informed.
        </Reveal>
        <Reveal delay={160}>
          <p className="text-muted-ink text-[16px] md:text-[17px] mt-6 md:mt-8 leading-[1.7] max-w-[560px] mx-auto">
            The best campaigns, creative trends and Producer&rsquo;s Notes,
            delivered occasionally.
          </p>
        </Reveal>
        <Reveal delay={240}>
          <form
            onSubmit={submit}
            className="mt-10 flex flex-col sm:flex-row items-stretch gap-3 max-w-[520px] mx-auto"
          >
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              disabled={state === "sending" || state === "done"}
              data-testid={JOURNAL.newsletterEmail}
              className="flex-1 h-12 px-5 rounded-full border border-bnm bg-white text-[14px] text-ink focus:outline-none focus:border-black transition-colors disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={state === "sending" || state === "done"}
              data-testid={JOURNAL.newsletterSubmit}
              className="denim-surface denim-stitch h-12 px-6 rounded-full text-white text-[14px] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-70"
            >
              <SubscribeButtonLabel state={state} />
            </button>
          </form>
        </Reveal>
        {state === "done" && (
          <p
            className="mt-4 text-[13px] text-muted-ink"
            data-testid={JOURNAL.newsletterSuccess}
          >
            We&rsquo;ll write when there&rsquo;s something worth reading.
          </p>
        )}
      </div>
    </section>
  );
}

export default function Journal() {
  const [featured, setFeatured] = useState(null);
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  // Initial: featured + categories
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [f, c] = await Promise.all([
          axios.get(`${API}/journal/featured`),
          axios.get(`${API}/journal/categories`),
        ]);
        if (!alive) return;
        setFeatured(f.data?.article || null);
        setCategories(c.data?.categories || []);
      } catch (err) {
        console.warn("journal featured/categories load failed:", err?.message);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // List, refetch when filters change
  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = {};
    if (activeCategory && activeCategory !== "All") params.category = activeCategory;
    if (q) params.q = q;
    params.limit = 48;
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/journal/articles`, { params });
        if (!alive) return;
        setArticles(data?.items || []);
      } catch (err) {
        console.warn("journal articles load failed:", err?.message);
      } finally {
        if (alive) setLoading(false);
      }
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [activeCategory, q]);

  const visibleArticles = useMemo(() => {
    if (!featured || activeCategory !== "All" || q) return articles;
    return articles.filter((a) => a.id !== featured.id);
  }, [articles, featured, activeCategory, q]);

  return (
    <div className="min-h-screen bg-white text-ink" data-testid={JOURNAL.page}>
      <Nav />

      {/* HERO */}
      <section data-testid={JOURNAL.hero}>
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-14 md:pb-20">
          <div className="max-w-[980px]">
            <Reveal>
              <div className="eyebrow eyebrow-denim mb-8">
                Brand New Money Insights
              </div>
            </Reveal>
            <Reveal as="h1" delay={80} className="font-display font-extrabold text-ink leading-[0.95] tracking-[-0.045em] text-[44px] sm:text-[68px] md:text-[92px] lg:text-[108px]">
              The <span className="font-serif-italic bnm-denim-shine font-light">stories</span>
              <br />
              shaping modern brands.
            </Reveal>
            <Reveal delay={180}>
              <p className="text-muted-ink text-[17px] md:text-[19px] leading-[1.65] mt-10 md:mt-12 max-w-[640px]">
                Every day we curate the world&rsquo;s most interesting campaigns,
                creative ideas, AI innovations and brand stories, so you
                don&rsquo;t have to.
              </p>
            </Reveal>
            <Reveal delay={280}>
              <div className="mt-10 flex flex-col sm:flex-row items-start gap-4">
                <a
                  href="#today"
                  className="btn-denim"
                  data-testid={JOURNAL.heroCta}
                >
                  <span>Explore Today&rsquo;s Stories</span>
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      {featured && (
        <section className="border-t border-bnm" data-testid={JOURNAL.featured}>
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-16 md:py-24">
            <div className="flex items-baseline justify-between mb-10 md:mb-14 gap-4">
              <Reveal>
                <div>
                  <div className="eyebrow eyebrow-denim mb-3">
                    Today&rsquo;s Featured Story
                  </div>
                  <h2 className="font-display font-extrabold text-[24px] md:text-[32px] tracking-[-0.03em] leading-[1.1] text-ink">
                    The one worth reading first.
                  </h2>
                </div>
              </Reveal>
              <Reveal delay={100}>
                <Link
                  to={`/journal/${featured.slug}`}
                  data-testid={JOURNAL.featuredLink}
                  className="text-[13.5px] text-muted-ink hover:text-ink transition-colors inline-flex items-center gap-2 shrink-0"
                >
                  Read story <ArrowRight className="w-4 h-4" />
                </Link>
              </Reveal>
            </div>
            <Reveal delay={100}>
              <div className="max-w-[1120px]">
                <ArticleCard a={featured} size="lg" />
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* CATEGORY BAR + SEARCH */}
      <section id="today" className="border-t border-bnm bg-white sticky top-[78px] md:top-[92px] z-30">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-4 md:py-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1">
              {[{ name: "All" }, ...categories].map((c) => {
                const active = activeCategory === c.name;
                return (
                  <button
                    key={c.name}
                    type="button"
                    onClick={() => setActiveCategory(c.name)}
                    data-testid={`${JOURNAL.categoryChip}-${c.name
                      .toLowerCase()
                      .replace(/\s+/g, "-")
                      .replace(/[^a-z0-9-]/g, "")}`}
                    aria-pressed={active}
                    className={`shrink-0 px-4 py-2 rounded-full border text-[13.5px] transition-colors ${
                      active
                        ? "denim-surface denim-stitch text-white border-transparent"
                        : "bg-white text-ink border-bnm hover:border-black"
                    }`}
                  >
                    {c.name}
                  </button>
                );
              })}
            </div>
            <div className="relative shrink-0 md:w-[280px]">
              <Search className="w-4 h-4 text-muted-ink absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search stories, brands, ideas"
                data-testid={JOURNAL.searchInput}
                className="w-full h-10 pl-10 pr-4 rounded-full border border-bnm text-[13.5px] text-ink focus:outline-none focus:border-black transition-colors"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ARTICLE GRID */}
      <section className="border-t border-bnm">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-16 md:py-20">
          <ArticleGrid loading={loading} articles={visibleArticles} />
        </div>
      </section>

      <Newsletter />
      <Footer />
    </div>
  );
}
